import type { ZodSchema } from "zod";
import { z } from "zod";
import type { Color} from "../color";
import { colorZodSchema } from "../color";
import { colorFactory } from "../color-factory";
import type { ColorSet} from "../color-sets/color-set";
import { colorSetZodSchema } from "../color-sets/color-set";
import type { ColorSetColor} from "../color-sets/color-set-names";
import { colorSetColors } from "../color-sets/color-set-names";
import {
    type ColorThemeSetName,
    colorThemeSetNames,
} from "../color-sets/color-theme-sets";
import type { HexColor } from "../hex-color/hex-color";
import type { HexColorSet } from "../hex-color/hex-color-set";
import type { HexColorTheme } from "../hex-color/hex-color-theme";

const colorSetNames = [
    "white",
    "black",
    "default",
    "transparent",
    "primary",
    "secondary",
    "tertiary",
    "quaternary",
    "purple",
    "blue",
    "orange",
    "lightGray",
    "warning",
    "information",
    "danger",
    "textInput",
    "disabled",
] as const;
const colorSetNameZodEnum = z.enum(colorSetNames);
type ColorSetName = (typeof colorSetNames)[number];

type ColorTheme = {
    _type: "COLOR_THEME";
    white: ColorSet;
    black: Color;
    transparent: Color;
    default: ColorSet;
    primary: ColorSet;
    secondary: ColorSet;
    tertiary: ColorSet;
    quaternary: ColorSet;
    purple: ColorSet;
    blue: ColorSet;
    orange: ColorSet;
    lightGray: ColorSet;
    warning: ColorSet;
    information: ColorSet;
    danger: ColorSet;
    textInput: ColorSet;
    disabled: ColorSet;
};
const colorThemeZodSchema = z.object({
    _type: z.literal("COLOR_THEME"),
    white: colorSetZodSchema,
    black: colorZodSchema,
    default: colorSetZodSchema,
    transparent: colorZodSchema,
    primary: colorSetZodSchema,
    secondary: colorSetZodSchema,
    tertiary: colorSetZodSchema,
    quaternary: colorSetZodSchema,
    purple: colorSetZodSchema,
    blue: colorSetZodSchema,
    orange: colorSetZodSchema,
    lightGray: colorSetZodSchema,
    warning: colorSetZodSchema,
    information: colorSetZodSchema,
    danger: colorSetZodSchema,
    textInput: colorSetZodSchema,
    disabled: colorSetZodSchema,
}) satisfies ZodSchema<ColorTheme>;

function colorThemeFactory(
    params: HexColorTheme,
    fallback: ColorTheme,
): ColorTheme {
    const colorTheme = Object.entries(params).reduce<ColorTheme>(
        (output, [hexColorThemeKey, hexColorSet]) => {
            // Only keep color theme set props, ignore the rest
            if (!colorThemeSetNames.some(name => name === hexColorThemeKey)) {
                return output;
            }
            const _colorThemeSetName = hexColorThemeKey as ColorThemeSetName;
            const _hexColorSet = hexColorSet as HexColorSet;
            for (const [key, value] of Object.entries(_hexColorSet)) {
                if (!colorSetColors.some(name => name === key)) {
                    continue;
                }
                const _key = key as ColorSetColor;
                const _hexColor = value as HexColor;
                const _color = colorFactory(_hexColor);

                output[_colorThemeSetName] = {
                    ...(output[_colorThemeSetName] || {}),
                    _type: "COLOR_SET",
                    [_key]: _color,
                    ...(_key === "main" ? { asString: _color.asString } : {}),
                };
            }
            return output;
        },
        {
            ...fallback,
        } as ColorTheme,
    );
    return colorTheme;
}

export {
    colorSetNames,
    colorSetNameZodEnum,
    colorThemeFactory,
    colorThemeZodSchema,
};
export type { ColorSet, ColorSetName, ColorTheme };
